import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import {
  Typography,
  Table,
  Button,
  Form,
  Input,
  DatePicker,
  Select,
  Space,
  Modal,
} from "antd";

const { Option } = Select;

export default function ViewHoliday() {
  const { backUrl, authData } = useContext(AuthContext);
  const [holidayData, setHolidayData] = useState([]);
  const [holidayOptions, setHolidayOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedYear, setSelectedYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [holidayName, setHolidayName] = useState(null);

  // Fetch holidays from backend
  const fetchHolidays = async ({
    yearOverride,
    monthOverride,
    fromOverride,
    toOverride,
    nameOverride,
  } = {}) => {
    setLoading(true);
    try {
      const y = yearOverride ?? selectedYear;
      const m = monthOverride ?? month;
      const f = fromOverride ?? fromDate;
      const t = toOverride ?? toDate;
      const n = nameOverride ?? holidayName;

      const params = {};
      if (y) params.from_year = y;
      if (m) params.month = m;
      if (f) params.from_date = f.format("YYYY-MM-DD");
      if (t) params.to_date = t.format("YYYY-MM-DD");
      if (n) params.holiday_name = n;

      const res = await axios.get(`${backUrl}/api/hr/holidays/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
        params,
      });

      const list = res.data.results ? res.data.results : res.data;
      setHolidayData(
        list.map((item) => ({
          ...item,
          holiday_date: item.holiday_date
            ? dayjs(item.holiday_date).format("YYYY-MM-DD")
            : null,
        }))
      );

      // Populate holiday name options
      const uniqueNames = [
        ...new Set(list.map((h) => h.holiday_name).filter(Boolean)),
      ];
      setHolidayOptions(uniqueNames);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch holidays!");
      setHolidayData([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on filter change
  useEffect(() => {
    if ((fromDate && !toDate) || (!fromDate && toDate)) return;
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, month, fromDate, toDate, holidayName]);

  // Month picker change
  const onMonthChange = (date) => {
    if (!date) {
      setMonth(null);
      return;
    }
    setMonth(date.month() + 1);
    if (!selectedYear) setSelectedYear(String(date.year()));
  };

  // Filters UI
  const FilterHolidays = () => {
    const monthValue = month
      ? dayjs(`${selectedYear || dayjs().year()}-${String(month).padStart(2, "0")}`, "YYYY-MM")
      : null;

    return (
      <div className="d-flex justify-content-center align-items-center pt-2" style={{ gap: 12 }}>
        {/* Year */}
        <DatePicker
          picker="year"
          placeholder="Select Year"
          value={selectedYear ? dayjs(selectedYear, "YYYY") : undefined}
          onChange={(date, dateString) => setSelectedYear(dateString || null)}
          style={{ width: 180 }}
          allowClear
        />

        {/* Month */}
        <DatePicker
          picker="month"
          placeholder="Select Month"
          value={monthValue || undefined}
          onChange={onMonthChange}
          style={{ width: 180 }}
          allowClear
        />

        {/* From Date */}
        <DatePicker
          placeholder="From Date"
          format="DD/MM/YYYY"
          value={fromDate || undefined}
          onChange={(date) => setFromDate(date)}
          style={{ width: 180 }}
          allowClear
        />

        {/* To Date */}
        <DatePicker
          placeholder="To Date"
          format="DD/MM/YYYY"
          value={toDate || undefined}
          onChange={(date) => setToDate(date)}
          style={{ width: 180 }}
          allowClear
        />

        {/* Holiday Name */}
        <Select
          showSearch
          allowClear
          placeholder="Select Holiday Name"
          value={holidayName || undefined}
          onChange={(value) => setHolidayName(value || null)}
          style={{ width: 220 }}
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {holidayOptions.map((name) => (
            <Option key={name} value={name}>
              {name}
            </Option>
          ))}
        </Select>
      </div>
    );
  };

  // Edit holidays
  const EditHolidays = () => {
    const [isEdit, setIsEdit] = useState(false);
    const [editData, setEditData] = useState(null);
    const [form] = Form.useForm();

    const handleEdit = (record) => {
      setEditData(record);
      setIsEdit(true);
      form.setFieldsValue({
        holiday_name: record.holiday_name,
        holiday_date: record.holiday_date ? dayjs(record.holiday_date, "YYYY-MM-DD") : null,
        year: record.year,
        holiday_day: record.holiday_day,
      });
    };

    const handleUpdate = async () => {
      try {
        const values = await form.validateFields();
        const payload = {
          ...values,
          id: editData.id,
          holiday_date: values.holiday_date.format("YYYY-MM-DD"),
        };

        await axios.put(`${backUrl}/api/hr/holidays/${editData.id}/`, payload, {
          headers: { Authorization: `Bearer ${authData.access}` },
        });

        toast.success("Holiday updated!");
        setIsEdit(false);
        fetchHolidays();
      } catch (err) {
        console.error(err);
        toast.error("Failed to update holiday!");
      }
    };

    const columns = [
      { title: "Year", dataIndex: "year", key: "year" },
      {
        title: "Month",
        dataIndex: "holiday_date",
        key: "month",
        render: (date) => (date ? dayjs(date).format("MMMM") : "-"),
      },
      { title: "Day", dataIndex: "holiday_day", key: "holiday_day" },
      {
        title: "Date",
        dataIndex: "holiday_date",
        key: "holiday_date",
        render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      },
      { title: "Name", dataIndex: "holiday_name", key: "holiday_name" },
      {
        title: "Update",
        key: "update",
        render: (_, record) => (
          <EditOutlined
            style={{ color: "#0e497b", fontSize: 18, cursor: "pointer" }}
            onClick={() => handleEdit(record)}
          />
        ),
      },
    ];

    return (
      <>
        <center>
          <FilterHolidays />
        </center>
        <br />
        <Table
          columns={columns}
          dataSource={holidayData}
          rowKey="id"
          loading={loading}
          bordered
          components={{
            header: {
              cell: (props) => (
                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                  {props.children}
                </th>
              ),
            },
          }}
        />
        <Modal
          title="Edit Holiday"
          open={isEdit}
          onCancel={() => setIsEdit(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsEdit(false)}>
              Cancel
            </Button>,
            <Button
              key="update"
              type="primary"
              style={{ backgroundColor: "#0e497b", color: "white" }}
              onClick={handleUpdate}
            >
              Update
            </Button>,
          ]}
          centered
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="holiday_name"
              label="Holiday Name"
              rules={[{ required: true, message: "Please enter holiday name" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="holiday_date"
              label="Holiday Date"
              rules={[{ required: true, message: "Please select holiday date" }]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="year" label="Year">
              <Input disabled />
            </Form.Item>
            <Form.Item name="holiday_day" label="Holiday Day">
              <Input disabled />
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  };

  return (
    <div style={{ padding: 20 }}>
      <center>
        <h5>Holiday List</h5>
      </center>
      <EditHolidays />
    </div>
  );
}
